import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../../config";
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper
} from "@mui/material";
import GenericTable from "../../../components/AdminDashboard/GenericTable/GenericTable";

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "N/A";
        const options = { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
        return date.toLocaleDateString('en-US', options);
    } catch (error) { return "N/A"; }
};

const formatEventType = (type) => {
    if (!type) return "N/A";
    const label = type.toLowerCase();
    return (
        <Chip 
            label={label} 
            size="small" 
            variant="outlined" 
            sx={{ 
                fontWeight: 500, 
                color: "#475569", 
                borderColor: "#94a3b8" 
            }} 
        />
    );
};

const JobLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDetails, setSelectedDetails] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.CLIENT_API_BASE_URL}/admin-jobs/logs`);
      setLogs(res.data || []);
    } catch (err) {
      console.error("Failed to fetch logs", err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const logColumns = [
    { id: "started_at", label: "Started At", render: (row) => formatDate(row.started_at) },
    { id: "job_name", label: "Job Name", render: (row) => <strong>{row.job_name}</strong> },
    { id: "status", label: "Status", render: (row) => formatEventType(row.status) },
    { id: "duration", label: "Duration", render: (row) => <span style={{ fontFamily: "monospace", color: "#64748b" }}>{row.duration_seconds ? Number(row.duration_seconds).toFixed(2) + "s" : "-"}</span> },
    { id: "actions", label: "Details", render: (row) => (
        <Button
            variant="text"
            size="small"
            onClick={() => setSelectedDetails({
                error: row.error_message,
                metrics: row.details
            })}
            sx={{ textTransform: "none", fontWeight: 600 }}
        >
            View JSON
        </Button>
    )}
  ];

  return (
    <div style={{ padding: "0" }}>
        <Box sx={{ px: 4, pt: 4, pb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", mb: 1, letterSpacing: "-0.5px" }}>
                Batch Job Traces
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: "800px" }}>
                Historical execution records and detailed metrics outputs of background operations.
            </Typography>
        </Box>

        <Box sx={{ p: 4, pt: 0 }}>
            {/* Minimal Filter Bar matching GatewayLogs arrangement */}
            <Box sx={{ 
                mb: 0, 
                p: 2, 
                backgroundColor: "#ffffff", 
                borderRadius: "8px 8px 0 0", 
                display: "flex", 
                gap: 2, 
                flexWrap: "wrap", 
                alignItems: "center", 
                border: "1px solid #e0e0e0", 
                borderBottom: "none" 
            }}>
                <Box sx={{ flexGrow: 1 }} />
                
                <Button 
                    variant="outlined" 
                    onClick={fetchLogs} 
                    sx={{ height: "40px", borderColor: "#1976d2", color: "#1976d2", "&:hover": { backgroundColor: "#f5f5f5" } }}
                >
                    <i className="material-symbols-outlined" style={{ marginRight: '8px', fontSize: '18px' }}>refresh</i>
                    Refresh Logs
                </Button>
            </Box>

            {!loading && (
                <GenericTable
                    title=""
                    columns={logColumns}
                    rows={logs}
                    isServerSide={false}
                    showView={false}
                    hideExport={true}
                />
            )}

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5, backgroundColor: "#fff", border: "1px solid #e0e0e0" }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>

        {/* JSON Viewer Dialog */}
        <Dialog open={!!selectedDetails} onClose={() => setSelectedDetails(null)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", fontWeight: 700, mb: 2 }}>
                Log JSON Output
            </DialogTitle>
            <DialogContent>
                {selectedDetails?.error && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="error" sx={{ fontWeight: 700, mb: 1 }}>Error Traceback:</Typography>
                        <Paper sx={{ backgroundColor: "#fef2f2", p: 2, overflowX: "auto" }} elevation={0}>
                            <pre style={{ margin: 0, fontSize: "12px", color: "#991b1b" }}>
                                {selectedDetails.error}
                            </pre>
                        </Paper>
                    </Box>
                )}
                
                {selectedDetails?.metrics && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Metrics Profile:</Typography>
                        <Paper sx={{ backgroundColor: "#1e1e1e", p: 2, overflowX: "auto" }} elevation={0}>
                            <pre style={{ margin: 0, fontSize: "13px", color: "#d4d4d4", fontFamily: "monospace" }}>
                                {JSON.stringify(selectedDetails.metrics, null, 2)}
                            </pre>
                        </Paper>
                    </Box>
                )}

                {!selectedDetails?.error && !selectedDetails?.metrics && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                        No structured details available for this log entry.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
                <Button onClick={() => setSelectedDetails(null)} variant="contained" disableElevation>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    </div>
  );
};

export default JobLogs;
